import React, { useState, useEffect, useMemo } from 'react';
import { AmountInput } from './AmountInput';
import {
  PhoneCall,
  Calendar,
  DollarSign,
  MapPin,
  FileText,
  MessageSquare,
  Sparkles,
  CheckCircle2,
  X,
  Send,
  AlertCircle,
  Percent,
  Receipt,
  Upload,
  HardDrive,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import { todayIso } from '../../utils/accountFilters';
import { isOTSExpired, evaluateOTSSlab } from '../../utils/otsScheme';
import { getLinkedAccounts } from '../../utils/linkedAccounts';
import { productOf } from '../../utils/collections';
import { OTSApplicationModal } from './OTSApplicationModal';
import { Account, FollowUpCallStatus, PaymentMode, VisitStatus, DocumentType } from '../../types';
import { formatINR } from '../../utils/watermark';
import { WhatsAppDraftModal } from './WhatsAppDraftModal';

import { todayLocal, toLocalDateString } from '../../utils/dates';
// -------------------------------------------------------------
// 1. Log Follow-up Modal
// -------------------------------------------------------------
export const LogFollowUpModal: React.FC<{
  account: Account;
  isOpen: boolean;
  onClose: () => void;
}> = ({ account, isOpen, onClose }) => {
  const { logFollowUp } = useSRMS();
  const [status, setStatus] = useState<FollowUpCallStatus>('Promised payment');
  const [customerResponse, setCustomerResponse] = useState<string>('Customer promised to pay overdue amount within 3 days.');
  const [agentRemarks, setAgentRemarks] = useState<string>('Follow-up scheduled to verify clearance.');
  const [nextFollowUpDate, setNextFollowUpDate] = useState<string>(
    toLocalDateString(Date.now() + 3 * 86400000)
  );
  const [nextFollowUpTime, setNextFollowUpTime] = useState<string>('11:00 AM');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    logFollowUp({
      accountId: account.accountId,
      status,
      customerResponse,
      // One merged field now. Saved in both places so existing screens and
      // reports that read either one still show the same text.
      discussionDetails: customerResponse,
      agentRemarks,
      nextFollowUpDate,
      nextFollowUpTime,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto font-sans">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg shadow-2xl text-slate-800 overflow-y-auto max-h-[92vh]">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-amber-100 text-amber-800 rounded-xl">
              <PhoneCall className="w-5 h-5" />
            </span>
            <div>
              <h3 className="font-bold text-sm text-slate-900">Record Customer Follow-up</h3>
              <p className="text-xs text-slate-500">{account.customerName} ({account.accountId})</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs">
          <div>
            <label className="block text-slate-700 font-semibold mb-1">Follow-up Call / Contact Status:</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as FollowUpCallStatus)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
            >
              <option value="Contacted">Contacted - Discussed</option>
              <option value="Promised payment">Promised Payment (PTP)</option>
              <option value="Paid">Paid on Call</option>
              <option value="Switched off">Switched Off / Unreachable</option>
              <option value="Unavailable">Customer Unavailable / Busy</option>
              <option value="Wrong number">Wrong Number / Invalid</option>
              <option value="Refused">Refused to Pay (Dispute)</option>
              <option value="Dispute">Loan Calculation Dispute</option>
              <option value="Not contacted">Not Contacted (No Answer)</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-slate-700 font-semibold mb-1">Customer Response &amp; Commitment:</label>
            <textarea
              rows={3}
              value={customerResponse}
              onChange={(e) => setCustomerResponse(e.target.value)}
              placeholder="What did the customer say, and what did they commit to? Include the discussion and terms."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-slate-700 font-semibold mb-1">Agent Internal Remarks:</label>
            <input
              type="text"
              value={agentRemarks}
              onChange={(e) => setAgentRemarks(e.target.value)}
              placeholder="Agent notes, next plan of action"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Next Follow-up Date:</label>
              <input
                type="date"
                value={nextFollowUpDate}
                onChange={(e) => setNextFollowUpDate(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Preferred Time:</label>
              <input
                type="text"
                value={nextFollowUpTime}
                onChange={(e) => setNextFollowUpTime(e.target.value)}
                placeholder="e.g. 11:00 AM"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="pt-2 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl border border-slate-200 font-medium cursor-pointer transition"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl shadow-sm cursor-pointer transition"
            >
              Save Follow-up &amp; Log to Timeline
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// -------------------------------------------------------------
// 2. Create PTP Modal (Promise To Pay)
// -------------------------------------------------------------
export const CreatePTPModal: React.FC<{
  account: Account;
  isOpen: boolean;
  onClose: () => void;
}> = ({ account, isOpen, onClose }) => {
  const { createPTP, accounts: allAccounts } = useSRMS();

  /**
   * ONE BORROWER, SEVERAL LOANS -- AND ONE PROMISE COVERING THEM.
   *
   * WHAT WAS WRONG. This window only ever knew about the account it was
   * opened from. A borrower with three loans promising to pay got a promise
   * recorded against ONE of them, for that one account's overdue, with no way
   * to say the promise covered the others. Collect Pay has handled linked
   * accounts for a long time; this screen never did. So the same customer's
   * promise and payment disagreed about how many loans existed.
   */
  const linkedAccounts = useMemo(() => {
    if (!account) return [];
    if (!allAccounts || allAccounts.length === 0) return [account];
    const matches = getLinkedAccounts(account, allAccounts);
    return matches.length > 0 ? matches : [account];
  }, [allAccounts, account]);

  const hasMultiple = linkedAccounts.length > 1;

  /**
   * Which loans this promise covers. All of them by default -- a borrower
   * saying "I will pay on Friday" means their debt, not one loan number they
   * have probably never seen.
   */
  const [coveredIds, setCoveredIds] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      setCoveredIds(linkedAccounts.map((a) => a.accountId));
    }
  }, [isOpen, account?.accountId, linkedAccounts.length]);

  const coveredAccounts = useMemo(
    () => linkedAccounts.filter((a) => coveredIds.includes(a.accountId)),
    [linkedAccounts, coveredIds]
  );

  /** What the ticked loans add up to. This is the figure that was missing. */
  const combinedOverdue = useMemo(
    () =>
      coveredAccounts.reduce(
        (sum, a) => sum + (Number(a.overdueAmount) || Number(a.outstandingAmount) || 0),
        0
      ),
    [coveredAccounts]
  );

  const [amount, setAmount] = useState<number>(account.overdueAmount || 15000);

  /**
   * The amount follows the ticked loans until the agent types their own
   * figure. After that it is theirs and is left alone -- a borrower promising
   * a part payment must not have it silently rewritten by ticking a box.
   */
  const [amountEdited, setAmountEdited] = useState(false);

  useEffect(() => {
    if (!amountEdited) setAmount(combinedOverdue);
  }, [combinedOverdue, amountEdited]);

  useEffect(() => {
    if (isOpen) setAmountEdited(false);
  }, [isOpen, account?.accountId]);

  const [ptpDate, setPtpDate] = useState<string>(
    toLocalDateString(Date.now() + 5 * 86400000)
  );
  const [ptpMode, setPtpMode] = useState<PaymentMode>('UPI');
  const [remarks, setRemarks] = useState<string>('');

  if (!isOpen) return null;

  const toggleCovered = (accountId: string) => {
    setCoveredIds((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const targets = coveredAccounts.length > 0 ? coveredAccounts : [account];
    const totalPromised = Number(amount) || 0;

    /**
     * ONE PROMISE, RECORDED AGAINST EACH LOAN IT COVERS.
     *
     * WHY NOT A SINGLE RECORD. Everything downstream is per account -- the
     * PTP count on a row, the "PTP Pending" status, the due-today reminder,
     * the broken-promise flag. A single record against one loan would leave
     * the borrower's other loans looking as though nothing had been promised,
     * and they would go on appearing in the follow-up list.
     *
     * HOW THE MONEY IS DIVIDED. In proportion to what each loan is overdue,
     * so a promise of 50,000 against loans owing 60,000 and 20,000 is
     * recorded as 37,500 and 12,500 -- not 25,000 each, which would overstate
     * the small loan and understate the large one.
     *
     * THE REMAINDER IS NOT LOST. Dividing rupees rarely comes out even, so
     * every part is floored and whatever is left over is added to the largest
     * share. The parts always sum to exactly what the borrower promised;
     * there is no rounding drift, in either direction.
     */
    const weights = targets.map(
      (a) => Number(a.overdueAmount) || Number(a.outstandingAmount) || 0
    );
    const weightTotal = weights.reduce((t, w) => t + w, 0);

    let shares: number[];
    if (weightTotal > 0) {
      shares = weights.map((w) => Math.floor((totalPromised * w) / weightTotal));
    } else {
      // No overdue recorded anywhere -- split evenly rather than refusing.
      const even = Math.floor(totalPromised / targets.length);
      shares = targets.map(() => even);
    }

    const allocated = shares.reduce((t, v) => t + v, 0);
    const remainder = totalPromised - allocated;
    if (remainder !== 0 && shares.length > 0) {
      let biggest = 0;
      for (let i = 1; i < shares.length; i += 1) {
        if (shares[i] > shares[biggest]) biggest = i;
      }
      shares[biggest] += remainder;
    }

    const coversMany = targets.length > 1;
    const loanList = targets.map((a) => a.accountId).join(', ');

    targets.forEach((target, i) => {
      createPTP({
        accountId: target.accountId,
        amount: shares[i],
        ptpDate,
        ptpMode,
        customerCommitment: coversMany
          ? `Promised ₹${totalPromised.toLocaleString('en-IN')} on ${ptpDate} via ${ptpMode}, covering ${targets.length} loan accounts (${loanList}). This account's share: ₹${shares[i].toLocaleString('en-IN')}.`
          : `Promised ₹${totalPromised.toLocaleString('en-IN')} on ${ptpDate} via ${ptpMode}.`,
        remarks: remarks.trim() || 'Committed payment recorded with borrower.',
      });
    });

    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto font-sans">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg shadow-2xl text-slate-800 overflow-y-auto max-h-[92vh]">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-purple-100 text-purple-800 rounded-xl">
              <Calendar className="w-5 h-5" />
            </span>
            <div>
              <h3 className="font-bold text-sm text-slate-900">Record Promise to Pay (PTP)</h3>
              <p className="text-xs text-slate-500">
                {account.customerName}
                {' • '}
                {/* The combined figure when the borrower holds several loans.
                    Showing one loan's overdue on a customer who owes on three
                    was the number an agent read out to them. */}
                Overdue: {formatINR(hasMultiple ? combinedOverdue : account.overdueAmount)}
                {hasMultiple && (
                  <span className="text-purple-600 font-semibold">
                    {' '}({coveredAccounts.length} of {linkedAccounts.length} loans)
                  </span>
                )}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs">
          {/*
            WHICH LOANS THIS PROMISE COVERS.

            Only shown when the borrower actually has more than one, so a
            single-loan customer sees exactly what they saw before.

            All are ticked by default: a borrower saying "I will pay on
            Friday" means their debt, not one loan number they have probably
            never been told. Untick any the promise does not cover.
          */}
          {hasMultiple && (
            <div className="border border-purple-200 bg-purple-50/50 rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-bold text-purple-900">
                  This borrower has {linkedAccounts.length} loan accounts
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setCoveredIds(
                      coveredIds.length === linkedAccounts.length
                        ? []
                        : linkedAccounts.map((a) => a.accountId)
                    )
                  }
                  className="text-purple-700 font-semibold hover:underline cursor-pointer"
                >
                  {coveredIds.length === linkedAccounts.length ? 'Clear all' : 'Select all'}
                </button>
              </div>

              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {linkedAccounts.map((la) => {
                  const ticked = coveredIds.includes(la.accountId);
                  return (
                    <label
                      key={`ptp-link-${la.accountId}`}
                      className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border cursor-pointer transition ${
                        ticked
                          ? 'bg-white border-purple-300'
                          : 'bg-slate-50 border-slate-200 opacity-70'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={ticked}
                        onChange={() => toggleCovered(la.accountId)}
                        className="rounded border-slate-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                      />
                      <span className="font-mono font-bold text-slate-700 flex-1">
                        {la.accountId}
                      </span>
                      <span className="text-slate-500">{productOf(la)}</span>
                      <span className="font-mono font-bold text-rose-600">
                        {formatINR(la.overdueAmount || la.outstandingAmount)}
                      </span>
                    </label>
                  );
                })}
              </div>

              <div className="flex items-center justify-between pt-1 border-t border-purple-200 font-bold text-purple-900">
                <span>Combined overdue on the ticked loans</span>
                <span className="font-mono">{formatINR(combinedOverdue)}</span>
              </div>

              {/* Said plainly, because the agent is about to tell the borrower
                  a number and should know what the app will record. */}
              <p className="text-[11px] text-purple-800/80 leading-snug">
                The promise is recorded against each ticked loan, divided in
                proportion to what each one is overdue. The parts always add up
                to exactly the amount promised.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Promised Amount (₹):</label>
              <AmountInput
                value={amount}
                onValueChange={(n) => {
                  setAmountEdited(true);
                  setAmount(n);
                }}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-purple-700 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono"
                required
                min={1}
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Promised Date:</label>
              <input
                type="date"
                value={ptpDate}
                onChange={(e) => setPtpDate(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-slate-700 font-semibold mb-1">Expected Payment Mode:</label>
            <select
              value={ptpMode}
              onChange={(e) => setPtpMode(e.target.value as PaymentMode)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
            >
              <option value="UPI">Online UPI (PhonePe / GPay / Paytm)</option>
              <option value="Cash">Cash Handover to Agent</option>
              <option value="Bank Transfer">Bank Transfer (NEFT / RTGS / IMPS)</option>
              <option value="Cheque">Cheque Deposit</option>
              <option value="POS Card">POS Card Swipe</option>
            </select>
          </div>

          <div>
            <label className="block text-slate-700 font-semibold mb-1">Remarks (Optional):</label>
            <input
              type="text"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="e.g. Borrower confirmed salary credit date"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div className="pt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl border border-slate-200 font-medium cursor-pointer transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl shadow-sm cursor-pointer transition"
            >
              Create PTP &amp; Set Reminder
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// -------------------------------------------------------------
// 3. Record Recovery & 10% Commission Modal (with OTS Scheme Support)
// -------------------------------------------------------------
export const RecordRecoveryModal: React.FC<{
  account: Account | null;
  relatedAccounts?: Account[];
  isOpen: boolean;
  onClose: () => void;
}> = ({ account, relatedAccounts, isOpen, onClose }) => {
  const { recordRecovery, commissionSettings, users, currentUser, accounts: allAccounts, liveDynamicCommission } = useSRMS();

  // Find all linked accounts for this customer
  const linkedAccounts = useMemo(() => {
    let base: Account[];
    if (relatedAccounts && relatedAccounts.length > 0) {
      base = relatedAccounts;
    } else if (!account) {
      base = [];
    } else if (!allAccounts || allAccounts.length === 0) {
      base = [account];
    } else {
      // One matching rule for the whole app. The copies here did not treat
      // "[NULL]" and "N/A" as empty, so blank customer codes matched each other.
      const matches = getLinkedAccounts(account, allAccounts);
      base = matches.length > 0 ? matches : [account];
    }
    // The list above was copied when the screen opened. Use the live version of
    // each account, so a change made since then (such as its category) is seen.
    if (!allAccounts || allAccounts.length === 0) return base;
    const live = new Map(allAccounts.map((a) => [a.accountId || a.id, a]));
    return base.map((a) => live.get(a.accountId || a.id) || a);
  }, [relatedAccounts, allAccounts, account]);

  const [selectedAccountId, setSelectedAccountId] = useState<string>(account?.accountId || '');

  useEffect(() => {
    if (account) {
      setSelectedAccountId(account.accountId);
    }
  }, [account?.accountId, isOpen]);

  const activeTargetAccount = useMemo(() => {
    return linkedAccounts.find((a) => a.accountId === selectedAccountId) || account;
  }, [linkedAccounts, selectedAccountId, account]);

  const targetModifiedOTS = useMemo(() => {
    if (!activeTargetAccount) return 0;
    return (
      activeTargetAccount.finalOTSAmount ||
      activeTargetAccount.userEnteredOTSAmount ||
      (activeTargetAccount.otsActive ? activeTargetAccount.otsAgreedAmount : 0) ||
      0
    );
  }, [activeTargetAccount]);

  /**
   * IS A SETTLEMENT ACTUALLY RUNNING ON THIS ACCOUNT?
   *
   * THE DISAGREEMENT THIS ENDS. This window decided "yes" if a settlement
   * FIGURE was stored -- `finalOTSAmount > 0` -- and did not look at whether
   * the settlement had ever been switched on. The OTS screen asks a
   * different question: `otsActive` AND a figure.
   *
   * M/S. DELICE BAKERY sat between the two answers. Rs 3,45,000 was saved on
   * it with `otsActive` still false, so the OTS screen correctly said "OTS
   * Not Applied" while this window opened with One-Time Settlement already
   * ticked, Rs 3,45,000 filled in and an "OTS ENABLED" badge. Two screens,
   * one record, opposite answers -- and the dangerous one was this window,
   * because recording against it would close an account as settled under a
   * settlement nobody had applied.
   *
   * Both screens now ask the same question. A figure saved but never applied
   * is NOT a running settlement; it is a draft, and the notice below says so
   * and sends the agent to the OTS screen to finish it.
   */
  const hasOTS = Boolean(
    (activeTargetAccount?.otsActive &&
      (activeTargetAccount?.finalOTSAmount || activeTargetAccount?.systemCalculatedOTSAmount)) ||
      activeTargetAccount?.otsStatus === 'OTS Applied' ||
      activeTargetAccount?.otsStatus === '10% OTS Paid - Sanction Pending'
  );

  /** A settlement figure is stored, but the settlement was never switched on. */
  const otsSavedButNotApplied = Boolean(targetModifiedOTS > 0 && !hasOTS);

  const [amount, setAmount] = useState<number>(() => {
    if (targetModifiedOTS > 0) return targetModifiedOTS;
    return activeTargetAccount?.overdueAmount || 20000;
  });
  /**
   * TODAY, IN INDIA -- not today in London.
   *
   * This was `todayLocal()`, which is the date in
   * UTC. India is five and a half hours ahead, so between midnight and half
   * past five in the morning that gives YESTERDAY'S date: at 2:15am on the
   * 30th the box read the 29th. A collection recorded in the early hours was
   * being dated to the previous day, and the agent had to notice and correct
   * it.
   *
   * It is also refreshed each time the window opens, so an app left running
   * overnight does not go on offering yesterday.
   */
  const [recoveryDate, setRecoveryDate] = useState<string>(() => todayIso());

  useEffect(() => {
    if (isOpen) setRecoveryDate(todayIso());
  }, [isOpen]);
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('UPI');
  const [referenceNumber, setReferenceNumber] = useState<string>(
    `UPI/${Date.now().toString().slice(-8)}/HDFC`
  );
  const [isOTS, setIsOTS] = useState<boolean>(hasOTS);
  const [otsStage, setOtsStage] = useState<'10_percent_token' | 'full_settlement'>('full_settlement');
  const [showOTSApplication, setShowOTSApplication] = useState(false);
  /**
   * THE SETTLEMENT FIGURE COMES FROM THE SCHEME, NOT FROM A GUESS.
   *
   * THE BUG THIS FIXES: when an account had no agreed settlement, ticking
   * One-Time Settlement here invented one at a flat SIXTY PER CENT of the
   * balance -- `outstanding * 0.6`, written into the code, with no reference
   * to the slabs, the customer's NPA date or the bank's scheme.
   *
   * So the OTS screen and this box disagreed on the same customer: one said
   * ₹3,31,938 at the 35% slab the NPA date actually falls in, the other said
   * 60% of the balance. The agent could collect against a figure the bank
   * never offered.
   *
   * Both now go through the same engine, so they cannot drift apart. If the
   * scheme is closed, or the NPA date falls in no slab, there is no figure --
   * because there is no settlement to collect against, and inventing one is
   * how the wrong amount got taken in the first place.
   */
  const slabForCollect = useMemo(() => {
    if (!activeTargetAccount) return null;
    const original =
      Number(activeTargetAccount.originalOutstandingAmount) ||
      Number(activeTargetAccount.outstandingAmount) ||
      0;
    return evaluateOTSSlab(
      original,
      activeTargetAccount.npaDate,
      (activeTargetAccount as any).bankName || (activeTargetAccount as any).bank
    );
  }, [
    activeTargetAccount?.accountId,
    activeTargetAccount?.originalOutstandingAmount,
    activeTargetAccount?.outstandingAmount,
    activeTargetAccount?.npaDate,
  ]);

  const [otsAgreedAmount, setOtsAgreedAmount] = useState<number>(
    targetModifiedOTS || activeTargetAccount?.otsAgreedAmount || 0
  );

  // Recovery Option: keep ONLY field agents (role === 'agent')
  // The field is `active`, not `isActive`. Reading the wrong name always gave
  // undefined, so `!== false` was always true and deactivated agents stayed
  // in the "collected by" list -- money could be credited to someone who had
  // been switched off.
  const agentUsers = users.filter((u) => u.role === 'agent' && u.active !== false);

  const [selectedAgentId, setSelectedAgentId] = useState<string>(() => {
    const assigned = agentUsers.find(
      (u) => u.id === activeTargetAccount?.assignedAgentId || u.agentId === activeTargetAccount?.assignedAgentId
    );
    if (assigned) return assigned.id;
    if (currentUser.role === 'agent') return currentUser.id;
    return agentUsers[0]?.id || '';
  });

  const isAdmin = currentUser.role === 'admin';

  // Guards against the collection being recorded twice. The ref is what
  // actually blocks it -- it latches in the same tick -- while the state
  // drives the button so the person can see the save is in progress.
  const isSubmittingRef = React.useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The latch MUST be cleared every time the modal opens.
  //
  // In the customer window this component is never unmounted -- it only
  // returns null while closed -- so the ref survived from one collection to
  // the next. After the first collection the latch stayed set, and every
  // collection after it showed "Recording..." and silently did nothing.
  useEffect(() => {
    if (isOpen) {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const [remarks, setRemarks] = useState<string>(() => {
    const base = 'Collected in full. Customer issued official electronic SRMS receipt acknowledgment.';
    return isAdmin ? `${base} [Admin Recovery: ${currentUser.name}]` : base;
  });

  // When active target account changes (e.g. user chooses another account in multi-account customer)
  useEffect(() => {
    if (activeTargetAccount) {
      /**
       * AN AGREED SETTLEMENT AND A POSSIBLE ONE ARE DIFFERENT THINGS.
       *
       * Only a settlement that has actually been agreed on this account
       * turns the tick box on and fills in the amount. What the scheme WOULD
       * allow is a suggestion, held ready in case somebody ticks the box
       * themselves -- it must never tick it for them.
       *
       * Getting this wrong opened Collect Pay with One-Time Settlement
       * already ticked and the discounted figure in the amount box, on an
       * account where no settlement had been agreed at all. An agent
       * pressing Record would have taken 35% of the balance and closed the
       * account as settled, without anyone choosing to settle anything.
       */
      const agreedOTS =
        activeTargetAccount.finalOTSAmount ||
        activeTargetAccount.userEnteredOTSAmount ||
        (activeTargetAccount.otsActive ? activeTargetAccount.otsAgreedAmount : 0) ||
        0;

      /**
       * Same question as the OTS screen asks -- see `hasOTS` above. A stored
       * figure on its own is a settlement that was never switched on, and
       * ticking One-Time Settlement for it is how an account gets closed
       * under a settlement nobody applied.
       */
      const otsActiveFlag = Boolean(
        (activeTargetAccount.otsActive &&
          (activeTargetAccount.finalOTSAmount ||
            activeTargetAccount.systemCalculatedOTSAmount)) ||
          activeTargetAccount.otsStatus === 'OTS Applied' ||
          activeTargetAccount.otsStatus === '10% OTS Paid - Sanction Pending'
      );

      setIsOTS(otsActiveFlag);

      if (otsActiveFlag && agreedOTS > 0) {
        setOtsAgreedAmount(agreedOTS);
        setAmount(agreedOTS);
      } else {
        /**
         * NO SETTLEMENT AGREED: THIS WINDOW NO LONGER INVENTS ONE.
         *
         * THE BUG THIS FIXES -- the Collect Pay window offered a figure the
         * borrower had never been promised.
         *
         * Seen on M/S. STAR MANDAP DECORATERS: the window showed an Agreed
         * Total OTS of Rs 34,645 with the stage set to "Final Settlement
         * Payment", against a real saved settlement of Rs 3,46,457 (35% of
         * Rs 9,89,878). Recording that would have closed the account as
         * settled for a TENTH of what was agreed -- a Rs 9,55,233 write-off.
         *
         * The cause was this line. When no settlement is saved on the
         * account, the window asked the slab engine for its own figure and
         * put it in the "Agreed Total OTS" box. That box is a RECORD OF WHAT
         * WAS AGREED. Filling it with a number nobody agreed to makes a
         * suggestion indistinguishable from an agreement, and the figure it
         * suggested could be out by a factor of ten depending on which
         * original the slab was evaluated against.
         *
         * The settlement figure now comes from ONE place: what was saved on
         * the OTS screen. If nothing is saved, the box stays EMPTY, the
         * guards in handleSubmit refuse an OTS collection, and the agent is
         * told to work the settlement out on the OTS screen first. That is
         * what Ashish asked for: "collect pay will reflect OTS scheme saved
         * amount, then proceed for further."
         *
         * `slabForCollect` is still used to SHOW what the scheme would
         * allow, as guidance beside the field -- it no longer fills it in.
         */
        setAmount(activeTargetAccount.overdueAmount || activeTargetAccount.outstandingAmount || 0);
        setOtsAgreedAmount(0);
      }

      const assigned = agentUsers.find(
        (u) => u.id === activeTargetAccount.assignedAgentId || u.agentId === activeTargetAccount.assignedAgentId
      );
      if (assigned) setSelectedAgentId(assigned.id);
    }
  }, [
    activeTargetAccount?.accountId,
    activeTargetAccount?.finalOTSAmount,
    activeTargetAccount?.userEnteredOTSAmount,
    activeTargetAccount?.otsActive,
    activeTargetAccount?.overdueAmount,
  ]);

  useEffect(() => {
    if (isOTS) {
      /**
       * The box is NOT filled in here either. Ticking One-Time Settlement by
       * hand used to drop the slab engine's own figure into "Agreed Total
       * OTS" -- see the note above. A settlement that has not been agreed
       * has no figure, and the window says so rather than inventing one.
       */
      if (otsStage === '10_percent_token') {
        const tokenVal = Math.round(otsAgreedAmount * 0.1);
        setAmount(tokenVal);
        const text = `10% OTS upfront token payment (₹${tokenVal.toLocaleString('en-IN')}) for agreed OTS ₹${otsAgreedAmount.toLocaleString('en-IN')}. Sanction process initiated.`;
        setRemarks(isAdmin ? `${text} [Admin Recovery: ${currentUser.name}]` : text);
      } else {
        // Everything already collected under the settlement comes off, not
        // just the 10% token. A customer who had paid two instalments was
        // still being asked for the whole balance less the token, so the
        // middle payments simply did not count.
        const paidSoFar =
          typeof activeTargetAccount?.otsPaidAmount === 'number'
            ? activeTargetAccount.otsPaidAmount
            : activeTargetAccount?.ots10PercentPaidAmount || 0;
        const remaining = Math.max(0, otsAgreedAmount - paidSoFar);
        setAmount(remaining);
        const text = `Full & final OTS settlement balance payment. Account settled and closed under OTS scheme.`;
        setRemarks(isAdmin ? `${text} [Admin Recovery: ${currentUser.name}]` : text);
      }
    }
  }, [
    isOTS,
    otsStage,
    otsAgreedAmount,
    activeTargetAccount?.ots10PercentPaidAmount,
    activeTargetAccount?.otsPaidAmount,
    isAdmin,
    currentUser.name,
  ]);

  if (!isOpen || !account || !activeTargetAccount) return null;

  // Batch 105: a bank on the new rules shows its saved rule; other banks keep today's rate.
  const dynAgentId = currentUser.role === 'agent' ? currentUser.id : selectedAgentId;
  const dynPreview = liveDynamicCommission({
    acc: activeTargetAccount,
    agentId: dynAgentId,
    amount,
    date: recoveryDate,
  });
  const dynReady = !!dynPreview && dynPreview.result.status === 'READY';
  const commissionRate = dynPreview
    ? dynReady
      ? dynPreview.result.rate ?? 0
      : 0
    : commissionSettings.defaultRate;
  const commissionEarned = dynPreview
    ? dynReady
      ? dynPreview.result.commission ?? 0
      : 0
    : Math.round((amount * commissionRate) / 100);
  const dynSuffix = !dynPreview
    ? ''
    : dynReady
      ? ` · ${dynPreview.ruleName}`
      : dynPreview.result.status === 'BLOCKED'
        ? ` · missing data, flagged: ${dynPreview.result.messages[0] ?? ''}`
        : dynPreview.result.status === 'CONFLICT'
          ? ' · two rules match, flagged for review'
          : ' · no rule matches, flagged for review';
  // An agent's collection is always credited to themselves, whatever the
  // (now hidden) selector happens to hold.
  const selectedAgent =
    currentUser.role === 'agent'
      ? currentUser
      : agentUsers.find((u) => u.id === selectedAgentId);
  const otsAlreadyPaid =
    typeof activeTargetAccount?.otsPaidAmount === 'number'
      ? activeTargetAccount.otsPaidAmount
      : activeTargetAccount?.ots10PercentPaidAmount || 0;
  const isAmountBelowOTS =
    isOTS &&
    otsStage === 'full_settlement' &&
    amount + otsAlreadyPaid < otsAgreedAmount;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // A second click used to record the collection twice. Worse than a plain
    // duplicate: by the time the second one ran, the first had already reduced
    // the outstanding amount, so the recovery stage flipped from "Partial" to
    // "Full Settlement" and a DIFFERENT commission rule -- a different
    // percentage -- was applied to the copy. The ref latches synchronously, so
    // two clicks queued in the same tick cannot both get through; React state
    // would not have updated in time.
    if (isSubmittingRef.current) return;

    // Nothing may be collected against a settled account, and nothing may be
    // collected beyond what is owed. A closed account was still accepting
    // money, which shows up later as a negative balance nobody can explain.
    const owed = isOTS
      ? Math.max(0, otsAgreedAmount - otsAlreadyPaid)
      : Math.max(
          activeTargetAccount?.outstandingAmount || 0,
          activeTargetAccount?.overdueAmount || 0
        );

    if (amount <= 0) {
      alert('Enter an amount greater than zero.');
      return;
    }

    /**
     * A COLLECTION UNDER A CLOSED SETTLEMENT IS REFUSED.
     *
     * The OTS screen already stops a new settlement being calculated once the
     * scheme has lapsed, but money was still being taken against one from
     * here -- which is the half that actually matters, because it is the
     * half involving the customer's money. A payment taken under a scheme the
     * bank has closed may not be honoured at the agreed discount.
     *
     * An ordinary collection against the full balance is unaffected; only the
     * settlement route is closed.
     */
    if (isOTS && otsAgreedAmount <= 0) {
      alert(
        'There is no settlement figure for this customer.\n\n' +
          (slabForCollect && !slabForCollect.slabDefined
            ? `${slabForCollect.statusMessage}\n\n`
            : '') +
          'Work the settlement out on the OTS screen first, or untick One-Time Settlement ' +
          'and record this as an ordinary collection.'
      );
      return;
    }

    if (isOTS) {
      const window_ = isOTSExpired(
        (activeTargetAccount as any)?.bankName || (activeTargetAccount as any)?.bank
      );
      if (window_.expired) {
        alert(
          `The OTS scheme for this bank closed on ${window_.lastDateIso}.\n\n` +
            'A payment cannot be taken as a settlement under a closed scheme. ' +
            'Ask an administrator to extend the date, or untick One-Time Settlement ' +
            'and record this as an ordinary collection against the full balance.'
        );
        return;
      }
    }

    if (owed <= 0) {
      alert(
        isOTS
          ? 'This settlement is already paid in full. There is nothing left to collect.'
          : 'This account has no outstanding balance. There is nothing left to collect.'
      );
      return;
    }

    /**
     * The over-collection question is asked ONCE, by recordRecovery.
     *
     * A near-identical confirm used to sit here as well, so a regularisation
     * produced two popups in a row asking the same thing -- and the second
     * one, in the context, was the one that actually refused. The context is
     * the right place for it: it is the only layer that cannot be bypassed,
     * and it is where the surplus is separated from the loan repayment.
     */

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    let finalRemarks = remarks.trim();
    if (isAdmin && !finalRemarks.includes('[Admin Recovery:')) {
      finalRemarks = `${finalRemarks} [Admin Recovery: ${currentUser.name}]`.trim();
    }

    recordRecovery({
      accountId: activeTargetAccount.accountId,
      amount,
      recoveryDate,
      paymentMode,
      referenceNumber,
      remarks: finalRemarks,
      isOTS,
      otsStage: isOTS ? otsStage : undefined,
      otsAgreedAmount: isOTS ? otsAgreedAmount : undefined,
      agentId: selectedAgent?.id,
      agentName: selectedAgent?.name,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto font-sans">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg shadow-2xl text-slate-800 overflow-y-auto max-h-[92vh] my-auto">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-emerald-100 text-emerald-800 rounded-xl">
              <DollarSign className="w-5 h-5" />
            </span>
            <div>
              <h3 className="font-bold text-sm text-slate-900">Log Loan Recovery Collection</h3>
              <p className="text-xs text-slate-500">
                {activeTargetAccount.customerName} • {activeTargetAccount.loanNumber || activeTargetAccount.accountId}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs">
          {/* Multi-Account Selector */}
          {linkedAccounts.length > 1 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl space-y-1.5">
              <label className="block text-blue-950 font-bold text-xs flex items-center justify-between">
                <span>Select Target Account for Recovery *</span>
                <span className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded font-bold">
                  {linkedAccounts.length} ACCOUNTS LINKED
                </span>
              </label>
              <select
                value={selectedAccountId}
                onChange={(e) => {
                  setSelectedAccountId(e.target.value);
                }}
                className="w-full bg-white border border-blue-200 rounded-lg px-3 py-2 text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              >
                {linkedAccounts.map((la) => (
                  <option key={la.accountId} value={la.accountId}>
                    {la.loanNumber || la.accountId} ({la.facility || la.bankName || 'Loan'}) — Bal: ₹{Number(la.originalOutstandingAmount || la.outstandingAmount || 0).toLocaleString('en-IN')} {la.finalOTSAmount ? `| Modified OTS: ₹${Number(la.finalOTSAmount).toLocaleString('en-IN')}` : ''}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-blue-700">
                Collection and OTS balance deductions will apply specifically to this selected account.
              </p>
            </div>
          )}
          {/* Admin Entry Callout with bracket remarks notice */}
          {isAdmin && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 flex items-start gap-2.5 text-xs">
              <span className="text-base">🛡️</span>
              <div>
                <span className="font-bold block">Admin Entry Mode</span>
                <span className="text-[11px] text-amber-800 leading-relaxed block">
                  Recovery entered by Administrator ({currentUser.name}). Bracket remarks <strong>[Admin Recovery: {currentUser.name}]</strong> will be recorded for audit trail.
                </span>
              </div>
            </div>
          )}

          {/* Who the collection is credited to.
              An AGENT collecting their own money has nothing to choose --
              it is theirs. Showing them a list of colleagues was not just
              clutter: it let one agent credit a collection, and its
              commission, to somebody else. Only a supervisor picks. */}
          {currentUser.role === 'agent' ? (
            <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs">
              <span className="block text-slate-500 font-semibold">
                Collecting agent
              </span>
              {/*
                THE BRANCH OF THE ACCOUNT, NOT THE AGENT'S OWN.

                This printed `currentUser.branch`, which is the single branch
                recorded against the agent's profile. An agent who covers
                several branches therefore saw one branch named on every
                collection, whichever account they were collecting against --
                so the line was wrong for every account outside that one
                branch, and the receipt it describes belongs to the ACCOUNT's
                branch, not the agent's.

                The agent's own branch is shown only as a fallback, for an
                account with no branch recorded.
              */}
              <span className="font-bold text-slate-900">
                {currentUser.name}
                {currentUser.agentId ? ` (${currentUser.agentId})` : ''}
                {(() => {
                  const branch =
                    (activeTargetAccount as any)?.branch ||
                    (activeTargetAccount as any)?.branchName ||
                    currentUser.branch;
                  return branch ? ` \u2022 ${branch}` : '';
                })()}
              </span>
            </div>
          ) : (
            <div>
              <label className="block text-slate-700 font-bold mb-1">
                Collecting Agent (Field Agents Only) *
              </label>
              <select
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 font-semibold focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
              >
                {agentUsers.length === 0 ? (
                  <option value="">No agents registered</option>
                ) : (
                  agentUsers.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name} ({agent.agentId || agent.id})
                      {agent.branch ? ` \u2022 ${agent.branch}` : ''}
                    </option>
                  ))
                )}
              </select>
              <p className="text-[10px] text-slate-500 mt-1">
                Field recovery agents only. The commission is credited to
                whoever is selected here.
              </p>
            </div>
          )}

          {/* OTS Scheme Toggle Box */}
          <div className="p-3.5 bg-gradient-to-r from-indigo-50/80 to-blue-50/80 border border-indigo-200 rounded-xl space-y-3">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer font-bold text-indigo-900 text-xs">
                <input
                  type="checkbox"
                  checked={isOTS}
                  onChange={(e) => setIsOTS(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                />
                <span>One-Time Settlement (OTS Scheme)</span>
              </label>
              {isOTS && (
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 font-bold text-[10px] rounded-full uppercase tracking-wider">
                  OTS Enabled
                </span>
              )}
            </div>

            {/* A figure is saved but the settlement was never switched on.
                This window used to treat that as a live settlement and tick
                the box; the OTS screen called the same account "OTS Not
                Applied". Now it says what is actually wrong. */}
            {isOTS && otsSavedButNotApplied && (
              <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-lg text-[11px] text-rose-900 font-semibold leading-relaxed">
                A settlement of ₹{targetModifiedOTS.toLocaleString('en-IN')} is saved on
                this account but has <span className="underline">not been applied</span> —
                the OTS screen shows it as “OTS Not Applied”. Open OTS Scheme and press
                “Save OTS Settlement” once to switch it on, then come back here. Until
                then this collection is treated as an ordinary payment.
              </div>
            )}

            {/* Multi-Account Customer notice */}
            {account.isMultipleAccount && isOTS && (
              <div className="p-2 bg-blue-100/70 border border-blue-300/80 rounded-lg text-[11px] text-blue-950 font-semibold">
                🔗 <strong>Multi-Account Merged Settlement:</strong> OTS applies to all linked customer accounts merged payment.
              </div>
            )}

            {isOTS && (
              <div className="space-y-3 pt-2 border-t border-indigo-100 text-xs">
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="block text-indigo-900 font-semibold mb-1">Agreed Total OTS (₹):</label>
                    <AmountInput
                      value={otsAgreedAmount}
                      onValueChange={setOtsAgreedAmount}
                      placeholder="No settlement saved"
                      className="w-full bg-white border border-indigo-200 rounded-lg px-2.5 py-1.5 font-mono font-bold text-indigo-900"
                    />
                    {/* Where the figure came from, said plainly. The window
                        used to fill this box with a figure it worked out
                        itself, which looked identical to an agreed one. */}
                    {otsAgreedAmount > 0 ? (
                      <p className="text-[10px] text-indigo-500 mt-1">
                        From the settlement saved on the OTS screen.
                      </p>
                    ) : (
                      <p className="text-[10px] text-rose-600 font-semibold mt-1">
                        Nothing saved on the OTS screen for this customer.
                        {slabForCollect?.systemCalculatedOTSAmount
                          ? ` The scheme would allow about ₹${slabForCollect.systemCalculatedOTSAmount.toLocaleString(
                              'en-IN'
                            )} — agree it there first.`
                          : ' Work it out on the OTS screen first.'}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-indigo-900 font-semibold mb-1">OTS Payment Stage:</label>
                    <select
                      value={otsStage}
                      onChange={(e) => setOtsStage(e.target.value as any)}
                      className="w-full bg-white border border-indigo-200 rounded-lg px-2.5 py-1.5 text-xs text-indigo-900 font-semibold"
                    >
                      <option value="10_percent_token">1. Upfront 10% Token (Sanction Pending)</option>
                      <option value="full_settlement">2. Final Settlement Payment</option>
                    </select>
                  </div>
                </div>

                {/* What has been paid against the settlement so far, and what
                    is left. Without this the person collecting had no way to
                    see whether earlier instalments had been counted. */}
                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <div className="p-2 bg-white rounded-lg border border-indigo-100">
                    <span className="block text-indigo-500 font-semibold">Agreed total</span>
                    <span className="font-mono font-bold text-indigo-900">
                      ₹{otsAgreedAmount.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="p-2 bg-white rounded-lg border border-indigo-100">
                    <span className="block text-indigo-500 font-semibold">Already paid</span>
                    <span className="font-mono font-bold text-emerald-700">
                      ₹{otsAlreadyPaid.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="p-2 bg-white rounded-lg border border-indigo-100">
                    <span className="block text-indigo-500 font-semibold">Balance left</span>
                    <span className="font-mono font-bold text-rose-700">
                      ₹{Math.max(0, otsAgreedAmount - otsAlreadyPaid).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>

                {isAmountBelowOTS && (
                  <div className="p-2.5 bg-amber-50 border border-amber-300 rounded-lg text-[11px] text-amber-900 leading-relaxed font-medium">
                    ⚠️ <strong>Partial Offer Note:</strong> The payment amount (₹{amount.toLocaleString('en-IN')}) is below the agreed OTS offer (₹{otsAgreedAmount.toLocaleString('en-IN')}). Account status will <strong>NOT</strong> be marked as closed till user closes it or clears balance.
                  </div>
                )}

                <p className="text-[11px] text-indigo-700 leading-relaxed">
                  {otsStage === '10_percent_token'
                    ? '💡 Customer deposits 10% token upfront. Once bank sanctions the OTS proposal, customer clears remaining balance to close account.'
                    : '🎉 When full agreed OTS is satisfied, account will be marked "Closed as per OTS". Authorized users can also close/delete the account.'}
                </p>
              </div>
            )}
          </div>

          {/* Recovery Date and Amount Input */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Payment Collection Date:</label>
              <input
                type="date"
                value={recoveryDate}
                onChange={(e) => setRecoveryDate(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 font-semibold"
                required
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Collection Amount (₹):</label>
              <AmountInput
                value={amount}
                onValueChange={setAmount}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-black text-emerald-600 font-mono focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
            </div>
          </div>

          {/* 10% Commission Calculation Live Callout */}
          <div className="p-3.5 bg-emerald-50/80 border border-emerald-200 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Percent className="w-5 h-5 text-amber-600" />
              <div>
                <span className="text-emerald-900 font-bold text-xs block">
                  Agent Incentive ({commissionRate}% Rate{dynSuffix}):
                </span>
                <span className="text-[11px] text-emerald-700">
                  {formatINR(amount)} × {commissionRate}% =
                </span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-base font-black text-amber-700 font-mono">
                {formatINR(commissionEarned)}
              </span>
              <span className="block text-[10px] text-emerald-800 font-semibold">
                Credited to {selectedAgent ? selectedAgent.name : 'Agent'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Payment Mode:</label>
              <select
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value as PaymentMode)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="UPI">UPI / QR Code</option>
                <option value="Cash">Cash Handover</option>
                <option value="Bank Transfer">Bank Transfer / IMPS</option>
                <option value="Cheque">Cheque</option>
                <option value="POS Card">POS Card Swipe</option>
              </select>
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Transaction Ref / Cheque No:</label>
              <input
                type="text"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder="e.g. UPI/7192834/HDFC"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-slate-700 font-semibold mb-1">
              Collection Remarks &amp; Receipt Note {isAdmin && <span className="text-amber-600 font-bold">[Admin Recovery in bracket]</span>}:
            </label>
            <textarea
              rows={2}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              required
            />
          </div>

          <div className="pt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl border border-slate-200 font-medium cursor-pointer transition"
            >
              Cancel
            </button>
            {/* The customer's own Marathi application to the bank. Shown only
                for an OTS collection, because the wording and the figures come
                from the settlement. */}
            {isOTS && (
              <button
                type="button"
                onClick={() => setShowOTSApplication(true)}
                className="px-4 py-2 bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50 font-bold rounded-xl transition flex items-center gap-1.5"
                title="ग्राहकाचा OTS अर्ज तयार करा (PDF)"
              >
                <FileText className="w-4 h-4" />
                <span>OTS अर्ज तयार करा</span>
              </button>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-sm flex items-center gap-1.5 cursor-pointer transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Receipt className="w-4 h-4" />
              <span>
                {isSubmitting
                  ? 'Recording…'
                  : 'Record Recovery & Issue Receipt'}
              </span>
            </button>
          </div>
        </form>
      </div>

      <OTSApplicationModal
        isOpen={showOTSApplication}
        onClose={() => setShowOTSApplication(false)}
        account={activeTargetAccount}
        relatedAccounts={linkedAccounts}
        stage={otsStage}
        agreedOtsAmount={
          Number(String(otsAgreedAmount).replace(/[^0-9.]/g, '')) ||
          activeTargetAccount.finalOTSAmount ||
          0
        }
        paidAmount={Number(String(amount).replace(/[^0-9.]/g, '')) || 0}
      />
    </div>
  );
};

// -------------------------------------------------------------
// 3B. Edit / Delete Recovery Modal
// -------------------------------------------------------------
export const EditRecoveryModal: React.FC<{
  recovery: any;
  isOpen: boolean;
  onClose: () => void;
}> = ({ recovery, isOpen, onClose }) => {
  const { editRecovery, deleteRecovery, currentUser } = useSRMS();
  const [amount, setAmount] = useState<number>(recovery?.amount || 0);
  const [recoveryDate, setRecoveryDate] = useState<string>(
    recovery?.recoveryDate || todayIso()
  );
  const [paymentMode, setPaymentMode] = useState<PaymentMode>(recovery?.paymentMode || 'UPI');
  const [referenceNumber, setReferenceNumber] = useState<string>(recovery?.referenceNumber || '');
  const [remarks, setRemarks] = useState<string>(recovery?.remarks || '');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (recovery) {
      setAmount(recovery.amount);
      setRecoveryDate(recovery.recoveryDate || todayIso());
      setPaymentMode(recovery.paymentMode || 'UPI');
      setReferenceNumber(recovery.referenceNumber || '');
      setRemarks(recovery.remarks || '');
    }
  }, [recovery]);

  if (!isOpen || !recovery) return null;

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    await editRecovery(recovery.id || recovery.recoveryId, {
      amount,
      recoveryDate,
      paymentMode,
      referenceNumber,
      remarks,
    });
    onClose();
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        `Delete collection ${recovery.receiptNumber} of ₹${recovery.amount.toLocaleString('en-IN')}?\n\nThe account balance and the agent's commission both go with it.`
      )
    ) {
      return;
    }

    setIsDeleting(true);
    /**
     * THE ANSWER IS NOW SHOWN.
     *
     * This called deleteRecovery and THREW THE RESULT AWAY. That function
     * reports what it did -- "the collection had already been deleted,
     * nothing was changed", "this collection was recorded by another user",
     * or the account's new balance -- and every one of those messages went
     * nowhere. The window simply closed, so a delete that had refused and a
     * delete that had worked looked identical.
     *
     * That is why a balance not moving was impossible to explain: the app
     * knew the reason and never said it.
     */
    const res = await deleteRecovery(recovery.id || recovery.recoveryId);
    setIsDeleting(false);

    if (!res?.success) {
      window.alert(res?.message || 'The collection could not be deleted.');
      return;
    }

    if (res.message) window.alert(res.message);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto font-sans">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg shadow-2xl text-slate-800 overflow-y-auto max-h-[92vh]">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-blue-100 text-blue-800 rounded-xl">
              <Receipt className="w-5 h-5" />
            </span>
            <div>
              <h3 className="font-bold text-sm text-slate-900">Edit / Delete Recovery Entry</h3>
              <p className="text-xs text-slate-500">{recovery.receiptNumber} • {recovery.customerName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleUpdate} className="p-6 space-y-4 text-xs">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Collection Date:</label>
              <input
                type="date"
                value={recoveryDate}
                onChange={(e) => setRecoveryDate(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Amount (₹):</label>
              <AmountInput
                value={amount}
                onValueChange={setAmount}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-emerald-700 font-mono focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Payment Mode:</label>
              <select
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value as PaymentMode)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="UPI">UPI</option>
                <option value="Cash">Cash</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cheque">Cheque</option>
                <option value="POS Card">POS Card</option>
              </select>
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-1">Reference No:</label>
              <input
                type="text"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 font-mono focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-slate-700 font-semibold mb-1">Remarks:</label>
            <textarea
              rows={2}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="pt-2 flex items-center justify-between">
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="px-3.5 py-2 bg-red-50 hover:bg-red-100 text-red-700 font-bold rounded-xl border border-red-200 cursor-pointer transition flex items-center gap-1"
            >
              <X className="w-3.5 h-3.5" />
              <span>Delete Entry</span>
            </button>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl border border-slate-200 font-medium cursor-pointer transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-sm cursor-pointer transition"
              >
                Save Changes
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// -------------------------------------------------------------
// 4. Start / Complete Field Visit Modal
// -------------------------------------------------------------
export const FieldVisitModal: React.FC<{
  account: Account;
  isOpen: boolean;
  onClose: () => void;
  onOpenWatermarkCamera?: () => void;
}> = ({ account, isOpen, onClose, onOpenWatermarkCamera }) => {
  const { activeVisit, startFieldVisit, completeFieldVisit } = useSRMS();
  const [status, setStatus] = useState<VisitStatus>('Customer Met');
  const [remarks, setRemarks] = useState<string>(
    'Met customer personally at residence. Verified address and obtained payment commitment.'
  );
  const [interaction, setInteraction] = useState<string>(
    'Customer explained business revenue dip. Cooperative.'
  );
  const [isStarting, setIsStarting] = useState(false);

  if (!isOpen) return null;

  const isCurrentActive = activeVisit && activeVisit.accountId === account.accountId;

  const handleStart = async () => {
    setIsStarting(true);
    await startFieldVisit(account.accountId);
    setIsStarting(false);
  };

  const handleComplete = () => {
    if (activeVisit) {
      completeFieldVisit(activeVisit.id, status, remarks, interaction);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto font-sans">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg shadow-2xl text-slate-800 overflow-y-auto max-h-[92vh]">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-emerald-100 text-emerald-800 rounded-xl">
              <MapPin className="w-5 h-5" />
            </span>
            <div>
              <h3 className="font-bold text-sm text-slate-900">
                {isCurrentActive ? 'Active Field Visit in Progress' : 'Start On-Ground Field Visit'}
              </h3>
              <p className="text-xs text-slate-500">{account.customerName} • {account.address}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 text-xs">
          {!isCurrentActive ? (
            <div className="space-y-4 text-center py-2">
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-left space-y-1.5">
                <div className="flex justify-between font-mono text-[11px]">
                  <span className="text-slate-500">Target Address:</span>
                  <span className="text-slate-800 font-semibold">{account.address}</span>
                </div>
                <div className="flex justify-between font-mono text-[11px]">
                  <span className="text-slate-500">Target GPS:</span>
                  <span className="text-emerald-700 font-bold">{account.latitude}° N, {account.longitude}° E</span>
                </div>
              </div>

              <p className="text-slate-600 text-xs">
                Starting the field visit will capture your live GPS coordinates, initialize visit audit timer, and allow one-click geo-tagged photo watermark capture.
              </p>

              <button
                type="button"
                disabled={isStarting}
                onClick={handleStart}
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-sm flex items-center justify-center gap-2 text-sm cursor-pointer transition"
              >
                <MapPin className="w-4 h-4" />
                <span>{isStarting ? 'Capturing GPS & Starting...' : 'Start Field Visit Now'}</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between">
                <div>
                  <span className="text-emerald-800 font-bold text-xs flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                    Visit Started at {activeVisit.startTime}
                  </span>
                  <span className="font-mono text-[11px] text-emerald-700 font-semibold">
                    GPS: {typeof activeVisit.latitude === 'number' ? activeVisit.latitude.toFixed(6) : '—'}° N, {typeof activeVisit.longitude === 'number' ? activeVisit.longitude.toFixed(6) : '—'}° E
                  </span>
                </div>
                {onOpenWatermarkCamera && (
                  <button
                    type="button"
                    onClick={onOpenWatermarkCamera}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs flex items-center gap-1 shadow-sm cursor-pointer transition"
                  >
                    <span>📸 Take Geo-Photo</span>
                  </button>
                )}
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Field Visit Outcome / Status:</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as VisitStatus)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="Customer Met">Customer Met Personally</option>
                  <option value="Door Locked">Door Locked (Neighbour Inquiry)</option>
                  <option value="Address Shifted">Address Shifted / Untraceable</option>
                  <option value="Payment Collected">Payment Collected on Spot</option>
                  <option value="PTP Obtained">PTP Commitment Obtained</option>
                  <option value="Neighbour Inquired">Neighbour Inquired</option>
                  <option value="Refused">Customer Refused Payment</option>
                </select>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Customer Interaction Details:</label>
                <textarea
                  rows={2}
                  value={interaction}
                  onChange={(e) => setInteraction(e.target.value)}
                  placeholder="Notes on customer attitude, asset inspection, co-borrower statement..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Final Visit Remarks for Audit:</label>
                <textarea
                  rows={2}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Summary remarks stamped to Cloud Firestore..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl border border-slate-200 font-medium cursor-pointer transition"
                >
                  Keep Active in Background
                </button>
                <button
                  type="button"
                  onClick={handleComplete}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-sm cursor-pointer transition"
                >
                  Complete &amp; Submit Field Visit
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------
// 5. Upload Document Modal (Direct Firestore Metadata Storage)
// -------------------------------------------------------------
export const UploadDocumentModal: React.FC<{
  account: Account;
  isOpen: boolean;
  onClose: () => void;
}> = ({ account, isOpen, onClose }) => {
  const { uploadDocument } = useSRMS();
  const [docType, setDocType] = useState<DocumentType>('PAN / Aadhaar KYC');
  const [fileName, setFileName] = useState<string>(`KYC_Proof_${account.accountId}.pdf`);
  const [fileSize, setFileSize] = useState<number>(340000);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setFileName(file.name);
      setFileSize(file.size);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUploading(true);
    try {
      await uploadDocument({
        accountId: account.accountId,
        documentType: docType,
        fileName,
        fileSizeBytes: selectedFile ? selectedFile.size : fileSize,
        file: selectedFile || undefined,
      });
      onClose();
    } catch (err) {
      console.error('Document upload error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto font-sans">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg shadow-2xl text-slate-800 overflow-y-auto max-h-[92vh]">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-indigo-100 text-indigo-800 rounded-xl">
              <FileText className="w-5 h-5" />
            </span>
            <div>
              <h3 className="font-bold text-sm text-slate-900">Attach Document / Evidence</h3>
              <p className="text-xs text-slate-500">{account.customerName} ({account.accountId})</p>
            </div>
          </div>
          <button onClick={onClose} disabled={isUploading} className="text-slate-400 hover:text-slate-700 cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs">
          {/* Cloud Firestore Storage Banner */}
          <div className="p-3 bg-emerald-50/80 border border-emerald-200 rounded-xl flex items-center justify-between gap-3 text-emerald-900">
            <div className="flex items-center gap-2.5">
              <ShieldCheck className="w-5 h-5 text-emerald-700 shrink-0" />
              <div>
                <p className="font-bold text-xs">Cloud Firestore Document Storage</p>
                <p className="text-[10px] text-emerald-700">Sub-50ms metadata synchronization with continuous cloud persistence</p>
              </div>
            </div>
            <span className="text-[10px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded border border-emerald-300">
              Live Real-Time
            </span>
          </div>

          <div>
            <label className="block text-slate-700 font-semibold mb-1">Document Category:</label>
            <select
              value={docType}
              onChange={(e) => {
                const type = e.target.value as DocumentType;
                setDocType(type);
                if (!selectedFile) {
                  setFileName(`${type.replace(/[^a-zA-Z0-9]/g, '_')}_${account.accountId}.pdf`);
                }
              }}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="PAN / Aadhaar KYC">PAN / Aadhaar KYC Proof</option>
              <option value="Salary / Income Proof">Salary / Income Proof / Bank Statement</option>
              <option value="Recovery Payment Receipt">Recovery Payment Receipt Slip</option>
              <option value="Visit Panchnama Proof">Visit Panchnama / Witness Proof</option>
              <option value="Legal Demand Notice">Legal Demand Notice / SARFAESI</option>
              <option value="Asset / Vehicle Photo">Asset / Vehicle Photo Proof</option>
              <option value="Dispute Settlement Form">Dispute Settlement Form</option>
            </select>
          </div>

          <div>
            <label className="block text-slate-700 font-semibold mb-1">File Name:</label>
            <input
              type="text"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
              required
            />
          </div>

          <div className="relative p-4 border-2 border-dashed border-slate-300 rounded-xl text-center bg-slate-50 space-y-2 hover:border-indigo-400 transition">
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={handleFileChange}
              className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
            />
            <Upload className="w-5 h-5 text-slate-400 mx-auto" />
            {selectedFile ? (
              <div>
                <p className="text-emerald-700 font-bold text-xs truncate max-w-xs mx-auto">{selectedFile.name}</p>
                <p className="text-[10px] text-slate-500">{(selectedFile.size / 1024).toFixed(1)} KB • Ready for Firestore Record</p>
              </div>
            ) : (
              <div>
                <p className="text-slate-700 font-medium text-xs">Select or Drag PDF / JPEG / PNG file</p>
                <p className="text-[10px] text-slate-400">Stores document record with verified timestamp and operator metadata</p>
              </div>
            )}
          </div>

          <div className="pt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isUploading}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl border border-slate-200 font-medium cursor-pointer transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUploading}
              className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold rounded-xl shadow-sm cursor-pointer transition flex items-center gap-1.5"
            >
              {isUploading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Saving Document...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Save Document to Firestore</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// -------------------------------------------------------------
// 6. WhatsApp Notification & Smart Draft Modal
// -------------------------------------------------------------
export const WhatsAppTriggerModal: React.FC<{
  account: Account;
  isOpen: boolean;
  onClose: () => void;
  defaultCategory?: 'PAYMENT_REMINDER' | 'LOK_ADALAT' | 'OTS_OFFER' | 'BROKEN_PTP' | 'FIELD_VISIT';
}> = WhatsAppDraftModal;

export { WhatsAppDraftModal };

// -------------------------------------------------------------
// 7. AI Recovery Pitch & Risk Analyzer Drawer
// -------------------------------------------------------------
export const AIRecoveryDrawer: React.FC<{
  account: Account;
  isOpen: boolean;
  onClose: () => void;
}> = ({ account, isOpen, onClose }) => {
  const { analyzeAccountAI } = useSRMS();
  const [loading, setLoading] = useState(false);
  const [aiData, setAiData] = useState<any | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadAIAnalysis();
    }
  }, [isOpen, account.accountId]);

  const loadAIAnalysis = async () => {
    setLoading(true);
    const data = await analyzeAccountAI(account.accountId);
    setAiData(data);
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/60 backdrop-blur-xs font-sans">
      <div className="w-full max-w-xl bg-white border-l border-slate-200 h-full flex flex-col shadow-2xl text-slate-800">
        <div className="p-5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-blue-600 rounded-xl text-white shadow-sm">
              <Sparkles className="w-5 h-5" />
            </span>
            <div>
              <h2 className="text-base font-bold text-slate-900">
                AI Recovery Intelligence &amp; Strategy
              </h2>
              <p className="text-xs text-blue-600 font-mono font-medium">
                {account.customerName} ({account.accountId})
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-xl transition cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-4 text-xs bg-[#f8fafc]">
          {loading ? (
            <div className="text-center py-16 space-y-3">
              <Sparkles className="w-8 h-8 text-blue-600 animate-spin mx-auto" />
              <p className="text-slate-800 font-semibold text-sm">Analyzing repayment history &amp; PTP commitments...</p>
              <p className="text-slate-500 text-xs">Formulating on-ground settlement strategy</p>
            </div>
          ) : aiData ? (
            <div className="space-y-4">
              {/* Risk Level Badge */}
              <div className="p-4 bg-white rounded-xl border border-slate-200 shadow-xs flex items-center justify-between">
                <div>
                  <span className="text-slate-500 text-[11px] block font-medium">Default Risk Assessment</span>
                  <span className="text-lg font-black text-red-600">{aiData.riskLevel} Risk</span>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-black font-mono text-amber-600">{aiData.riskScore}/100</span>
                  <span className="text-[10px] text-slate-500 block">Risk Score</span>
                </div>
              </div>

              {/* Recommended Action */}
              <div className="p-4 bg-blue-50/70 rounded-xl border border-blue-200 space-y-1.5">
                <span className="text-blue-800 font-bold uppercase tracking-wider text-[11px] block">
                  🎯 Recommended Field Agent Action
                </span>
                <p className="text-slate-700 text-xs leading-relaxed font-medium">
                  {aiData.recommendedAction}
                </p>
              </div>

              {/* Settlement Strategy */}
              <div className="p-4 bg-purple-50/70 rounded-xl border border-purple-200 space-y-1.5">
                <span className="text-purple-800 font-bold uppercase tracking-wider text-[11px] block">
                  💡 Settlement &amp; Installment Strategy
                </span>
                <p className="text-slate-700 text-xs leading-relaxed font-medium">
                  {aiData.settlementStrategy}
                </p>
              </div>

              {/* Field Visit Tip */}
              <div className="p-4 bg-amber-50/70 rounded-xl border border-amber-200 space-y-1.5">
                <span className="text-amber-800 font-bold uppercase tracking-wider text-[11px] block">
                  📍 In-Person Negotiation Talk-Track
                </span>
                <p className="text-slate-700 text-xs leading-relaxed font-medium">
                  {aiData.fieldVisitTip}
                </p>
              </div>

              {/* WhatsApp Script */}
              <div className="p-4 bg-white rounded-xl border border-slate-200 shadow-xs space-y-2">
                <span className="text-emerald-700 font-bold uppercase tracking-wider text-[11px] block">
                  💬 Tailored Payment Script
                </span>
                <p className="text-slate-700 font-mono text-xs p-3 bg-slate-50 rounded-lg border border-slate-200 whitespace-pre-line leading-relaxed">
                  {aiData.whatsappScript}
                </p>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------
// 7. Add Agent Note Modal
// -------------------------------------------------------------
export const AddAgentNoteModal: React.FC<{
  account: Account;
  isOpen: boolean;
  onClose: () => void;
}> = ({ account, isOpen, onClose }) => {
  const { addAgentNote, currentUser } = useSRMS();
  const [noteCategory, setNoteCategory] = useState<string>('General');
  const [noteText, setNoteText] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteText.trim()) return;

    setIsSubmitting(true);
    addAgentNote(account.accountId, noteText.trim(), noteCategory);
    setIsSubmitting(false);
    setNoteText('');
    onClose();
  };

  const quickNotes = [
    'Customer promised repayment by next week.',
    'Met customer at business premise. Shop operational.',
    'Customer requested loan restructuring / interest waiver.',
    'Customer unreachable on phone, neighbor confirmed residence.',
    'Customer disputed interest calculation. Escalated to branch manager.',
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto font-sans">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg shadow-2xl text-slate-800 overflow-y-auto max-h-[92vh] animate-in fade-in zoom-in duration-150">
        <div className="px-6 py-4 bg-[#0f172a] text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-blue-500/20 text-blue-300 border border-blue-400/30 rounded-xl">
              <MessageSquare className="w-5 h-5" />
            </span>
            <div>
              <h3 className="font-bold text-sm text-white">Add Agent Note &amp; Remark</h3>
              <p className="text-xs text-slate-300">
                {account.customerName} • {account.loanNumber}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white cursor-pointer transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs">
          {/* Category */}
          <div>
            <label className="block text-slate-700 font-semibold mb-1">Note Category / Tag:</label>
            <select
              value={noteCategory}
              onChange={(e) => setNoteCategory(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
            >
              <option value="General">General Note</option>
              <option value="PTP Commitment">PTP / Payment Commitment</option>
              <option value="Field Visit">Field Visit Observation</option>
              <option value="Follow Up">Follow-up Call Remark</option>
              <option value="Customer Dispute">Borrower Dispute / Calculation</option>
              <option value="Legal / Notice">Legal Notice &amp; Arbitration</option>
              <option value="Payment Collection">Payment Receipt Note</option>
              <option value="Skipped / Untraceable">Customer Untraceable / Skipped</option>
            </select>
          </div>

          {/* Quick Pre-fill Chips */}
          <div>
            <label className="block text-slate-500 font-medium mb-1.5 text-[11px]">Quick Pre-fill Templates:</label>
            <div className="flex flex-wrap gap-1.5">
              {quickNotes.map((qn, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setNoteText(qn)}
                  className="text-[10px] bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-600 px-2.5 py-1 rounded-lg border border-slate-200 transition text-left cursor-pointer"
                >
                  + {qn}
                </button>
              ))}
            </div>
          </div>

          {/* Note input */}
          <div>
            <label className="block text-slate-700 font-semibold mb-1">Agent Detailed Note &amp; Ground Intel:</label>
            <textarea
              rows={4}
              required
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Enter exact customer statement, current whereabouts, asset conditions, promises made, or recovery instructions..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-slate-400"
            />
          </div>

          {/* Agent info banner */}
          <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-slate-600 flex items-center justify-between text-[11px]">
            <span>
              Author: <strong>{currentUser.name}</strong> ({currentUser.agentId || currentUser.role})
            </span>
            <span>Date: {new Date().toLocaleDateString('en-IN')}</span>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!noteText.trim() || isSubmitting}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs shadow-sm flex items-center gap-1.5 transition cursor-pointer disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              <span>Save Agent Note</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
